// hooks/useChatbotQueries.js
// React Query hooks for the WhatsApp Chatbot dashboard page.
// Same shape as useWorkshopQueries.js — shared `api` axios instance,
// centralized KEYS, defensive field normalization on every response.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import api from "../axiosInstance";

export const KEYS = {
  chatbotActive: () => ["chatbot-active"],
  chatbotQueue: () => ["chatbot-queue"],
  chatbotMine: () => ["chatbot-mine"],
  chatbotThread: (sessionId) => ["chatbot-thread", sessionId],
};

// ─── Normalizers ──────────────────────────────────────────────────────────────

function normalizeChat(c) {
  return {
    sessionId: c.sessionId,
    phoneNumber: c.phoneNumber || null,
    caseNo: c.caseNo || null,
    currentFlow: c.currentFlow || null,
    currentStep: c.currentStep || null,
    sessionStatus: c.sessionStatus || null,
    controlMode: c.controlMode || "BOT",
    assignedAgentId: c.assignedAgentId || null,
    assignedAgentCode: c.assignedAgentCode || null,
    assignedAgentName: c.assignedAgentName || null,
    isMine: !!c.isMine,
    agentJoinedAt: c.agentJoinedAt || null,
    lastMessageAt: c.lastMessageAt || null,
    lastInboundAt: c.lastInboundAt || null,
    lastMessagePreview: c.lastMessagePreview || null,
    lastMessageDirection: c.lastMessageDirection || null,
  };
}

function normalizeMessage(m) {
  let metadata = m.metadata || null;
  if (typeof metadata === "string") {
    try {
      metadata = JSON.parse(metadata);
    } catch {
      metadata = null;
    }
  }
  return {
    id: m.id,
    sessionId: m.sessionId,
    phoneNumber: m.phoneNumber || null,
    caseNo: m.caseNo || null,
    direction: m.direction || null,
    messageType: m.messageType || null,
    whatsappMessageId: m.whatsappMessageId || null,
    content: m.content || "",
    mediaUrl: m.mediaUrl || null,
    deliveryStatus: m.deliveryStatus || null,
    metadata,
    createdAt: m.createdAt || null,
  };
}

// ─── QUERIES ──────────────────────────────────────────────────────────────────

export function useActiveChats() {
  return useQuery({
    queryKey: KEYS.chatbotActive(),
    queryFn: async () => {
      const { data } = await api.get("/api/whatsapp/dashboard/active");
      const raw = data?.data?.chats || [];
      return raw.map(normalizeChat);
    },
    refetchInterval: 30000, // fallback polling — WS pings trigger faster invalidation
  });
}

export function useQueueChats() {
  return useQuery({
    queryKey: KEYS.chatbotQueue(),
    queryFn: async () => {
      const { data } = await api.get("/api/whatsapp/dashboard/queue");
      const raw = data?.data?.chats || [];
      return raw.map(normalizeChat);
    },
    refetchInterval: 30000,
  });
}

export function useMyChats() {
  return useQuery({
    queryKey: KEYS.chatbotMine(),
    queryFn: async () => {
      const { data } = await api.get("/api/whatsapp/dashboard/mine");
      const raw = data?.data?.chats || [];
      return raw.map(normalizeChat);
    },
    refetchInterval: 30000,
  });
}

export function useChatThread(sessionId, dateFilter) {
  return useQuery({
    queryKey: KEYS.chatbotThread(sessionId),
    queryFn: async () => {
      const params = {};
      if (dateFilter?.fromDate) params.fromDate = dateFilter.fromDate;
      if (dateFilter?.toDate) params.toDate = dateFilter.toDate;
      const { data } = await api.get(
        `/api/whatsapp/dashboard/${sessionId}/messages`,
        { params }
      );
      const raw = data?.data?.messages || [];
      return {
        messages: raw.map(normalizeMessage),
        session: data?.data?.session ? normalizeChat(data.data.session) : null,
      };
    },
    enabled: !!sessionId,
    refetchInterval: sessionId ? 8000 : false,
  });
}

export function useMediaBlob(mediaId) {
    return useQuery({
        queryKey: ["chatbot-media", mediaId],
        queryFn: async () => {
        const res = await api.get(`/api/whatsapp/dashboard/media/${mediaId}`, {
            responseType: "blob",
        });
        return res.data; // Blob
        },
        enabled: !!mediaId,
        staleTime: Infinity,
        gcTime: 1000 * 60 * 30,
    });
}

// ─── MUTATIONS ────────────────────────────────────────────────────────────────

function invalidateChatbotLists(qc, sessionId) {
  qc.invalidateQueries({ queryKey: KEYS.chatbotQueue() });
  qc.invalidateQueries({ queryKey: KEYS.chatbotActive() });
  qc.invalidateQueries({ queryKey: KEYS.chatbotMine() });
  if (sessionId)
    qc.invalidateQueries({ queryKey: KEYS.chatbotThread(sessionId) });
}

export function useClaimChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId) => {
      const { data } = await api.post(
        `/api/whatsapp/dashboard/${sessionId}/claim`,
      );
      return data;
    },
    onSuccess: (_data, sessionId) => invalidateChatbotLists(qc, sessionId),
  });
}

export function useReleaseChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, notifyCustomer = true }) => {
      const { data } = await api.post(
        `/api/whatsapp/dashboard/${sessionId}/release`,
        { notifyCustomer },
      );
      return data;
    },
    onSuccess: (_data, { sessionId }) => invalidateChatbotLists(qc, sessionId),
  });
}

export function useSendAgentMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, text }) => {
      const { data } = await api.post(
        `/api/whatsapp/dashboard/${sessionId}/message`,
        { text },
      );
      return data;
    },
    onSuccess: (_data, { sessionId }) => {
      qc.invalidateQueries({ queryKey: KEYS.chatbotThread(sessionId) });
      qc.invalidateQueries({ queryKey: KEYS.chatbotActive() });
    },
  });
}

// ─── LIVE UPDATES (WebSocket) ─────────────────────────────────────────────────
// Connects the same way the rest of the admin panel's sockets do — sends
// { type: "firstJoin", userId } with the EMPID-prefixed id already in
// localStorage, then invalidates the relevant query on any "whatsapp-dashboard"
// ping instead of trying to merge deltas into local state.
//
// ASSUMPTION: WS_URL isn't available from axiosInstance (that's HTTP-only), so
// this still needs its own endpoint. Point it at wherever setupWebSocket's
// server actually listens — same host as your API, same port as app.js.

const WS_URL =
  process.env.REACT_APP_WS_URL ||
  `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.hostname}:3001`;

export function useWhatsappDashboardSocket() {
  const qc = useQueryClient();
  const wsRef = useRef(null);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;

    let socket;
    let heartbeat;
    let cancelled = false;

    function connect() {
      socket = new WebSocket(WS_URL);
      wsRef.current = socket;

      socket.onopen = () => {
        socket.send(JSON.stringify({ type: "firstJoin", userId }));
        heartbeat = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: "ping" }));
          }
        }, 25000);
      };

      socket.onmessage = (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }
        if (data.type !== "whatsapp-dashboard") return;
        invalidateChatbotLists(qc, data.sessionId);
      };

      socket.onclose = () => {
        clearInterval(heartbeat);
        if (!cancelled) setTimeout(connect, 3000); // basic reconnect
      };
    }

    connect();

    return () => {
      cancelled = true;
      clearInterval(heartbeat);
      wsRef.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
